import React from 'react'
import { Box, Typography, Divider, Button } from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import {
    ezScheduleDetails,
    TemplateUnit,
} from '../../../../../models/WorkOrder/types'
import { useLoriReviewStyles } from '../styles'
import { useTimezone } from '../../../../../contexts/timezone/timezone'
import { DateTime } from 'luxon'
import { EzosLoader } from '../../../../../components/EzosLoader/EzosLoader'

interface FolderNode {
    name: string
    unitCount: number
    children: Map<string, FolderNode>
}

const buildFolderTree = (units: TemplateUnit[]): FolderNode => {
    const root: FolderNode = { name: 'Root', unitCount: 0, children: new Map() }

    for (const tu of units) {
        const folder = tu.unit.folder
        const parentSegments = folder.path
            .split('/')
            .filter((s: string) => s !== '')
        parentSegments.push(folder.name)

        let current = root
        for (const segment of parentSegments) {
            if (!current.children.has(segment)) {
                current.children.set(segment, {
                    name: segment,
                    unitCount: 0,
                    children: new Map(),
                })
            }
            current = current.children.get(segment)!
        }
        current.unitCount++
    }

    return root
}

const getTotalUnitCount = (node: FolderNode): number => {
    if (node.children.size === 0) return node.unitCount
    let total = node.unitCount
    node.children.forEach((child) => {
        total += getTotalUnitCount(child)
    })
    return total
}

interface FolderTreeNodeProps {
    node: FolderNode
    depth: number
    classes: ReturnType<typeof useLoriReviewStyles>
}

const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({
    node,
    depth,
    classes,
}) => {
    const isLeaf = node.children.size === 0
    const totalCount = getTotalUnitCount(node)
    const sortedChildren = Array.from(node.children.values()).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
    )
    const allChildrenAreLeaves = sortedChildren.every(
        (c) => c.children.size === 0,
    )

    return (
        <Box>
            <Box
                className={classes.folderRow}
                style={{ paddingLeft: depth * 16 }}
            >
                <Typography
                    className={
                        isLeaf
                            ? classes.folderLeafName
                            : classes.folderBranchName
                    }
                >
                    {node.name}
                </Typography>
                {totalCount > 0 && (
                    <Typography className={classes.folderUnitCount}>
                        {totalCount} {totalCount === 1 ? 'unit' : 'units'}
                    </Typography>
                )}
            </Box>
            {sortedChildren.length > 0 && (
                <Box
                    className={
                        allChildrenAreLeaves ? classes.folderLeafRow : undefined
                    }
                    style={
                        allChildrenAreLeaves
                            ? { paddingLeft: (depth + 1) * 16 }
                            : undefined
                    }
                >
                    {sortedChildren.map((child) => (
                        <FolderTreeNode
                            key={child.name}
                            node={child}
                            depth={allChildrenAreLeaves ? 0 : depth + 1}
                            classes={classes}
                        />
                    ))}
                </Box>
            )}
        </Box>
    )
}

interface Props {
    ezSchedule: ezScheduleDetails
    onProceedToReview?: () => void
    phaseId: number
    isLoading?: boolean
}

const formatDateTz = (
    dateStr: string | null | undefined,
    timezone: string,
): string => {
    if (!dateStr) return 'Not set'
    const dt = DateTime.fromISO(dateStr, { zone: 'utc' }).setZone(timezone)
    return dt.isValid ? dt.toFormat('MM/dd/yyyy') : 'Invalid date'
}

export const LoriReview = (props: Props) => {
    const { ezSchedule, onProceedToReview, phaseId, isLoading } = props
    const classes = useLoriReviewStyles()
    const { currentTimezone } = useTimezone()

    if (isLoading) {
        return (
            <Box className={classes.root} style={{ minHeight: 200 }}>
                <EzosLoader />
            </Box>
        )
    }

    const selectedPhase = ezSchedule.phases.find((p) => p.phase.id === phaseId)

    const includedUnits =
        selectedPhase?.template_units?.filter((tu) => tu.is_included) ?? []
    const hasWorkDates =
        selectedPhase?.work_start_date != null &&
        selectedPhase?.work_end_date != null
    const hasTransferDates =
        selectedPhase?.transfer_start_date != null ||
        selectedPhase?.transfer_end_date != null
    const templateServices = selectedPhase?.template_services ?? []

    const folderTree = buildFolderTree(includedUnits)

    return (
        <Box className={classes.root}>
            <Box className={classes.headerRow}>
                <Typography className={classes.header}>
                    Schedule Summary
                </Typography>
                {onProceedToReview && (
                    <Button
                        onClick={onProceedToReview}
                        className={classes.proceedButton}
                        startIcon={<ArrowForwardIcon />}
                    >
                        Proceed to Review
                    </Button>
                )}
            </Box>

            {/* Date Ranges Section */}
            {(hasWorkDates || hasTransferDates) && (
                <Box className={classes.section}>
                    <Typography className={classes.sectionHeader}>
                        Dates
                    </Typography>
                    {hasWorkDates && (
                        <Box className={classes.dateRow}>
                            <Typography className={classes.dateLabel}>
                                Work:
                            </Typography>
                            <Typography className={classes.dateValue}>
                                {formatDateTz(
                                    selectedPhase?.work_start_date,
                                    currentTimezone,
                                )}{' '}
                                -{' '}
                                {formatDateTz(
                                    selectedPhase?.work_end_date,
                                    currentTimezone,
                                )}
                            </Typography>
                        </Box>
                    )}
                    {hasTransferDates && (
                        <Box className={classes.dateRow}>
                            <Typography className={classes.dateLabel}>
                                Transfer:
                            </Typography>
                            <Typography className={classes.dateValue}>
                                {formatDateTz(
                                    selectedPhase?.transfer_start_date,
                                    currentTimezone,
                                )}{' '}
                                -{' '}
                                {formatDateTz(
                                    selectedPhase?.transfer_end_date,
                                    currentTimezone,
                                )}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Units Section */}
            {includedUnits.length > 0 && (
                <>
                    <Divider className={classes.divider} />
                    <Box className={classes.section}>
                        <Typography className={classes.sectionHeader}>
                            Selected Units ({includedUnits.length})
                        </Typography>
                        <Box className={classes.folderTree}>
                            {Array.from(folderTree.children.values())
                                .sort((a, b) =>
                                    a.name.localeCompare(b.name, undefined, {
                                        numeric: true,
                                    }),
                                )
                                .map((child) => (
                                    <FolderTreeNode
                                        key={child.name}
                                        node={child}
                                        depth={0}
                                        classes={classes}
                                    />
                                ))}
                        </Box>
                    </Box>
                </>
            )}

            {/* Services Section */}
            {templateServices.length > 0 && (
                <>
                    <Divider className={classes.divider} />
                    <Box className={classes.section}>
                        <Typography className={classes.sectionHeader}>
                            Services ({templateServices.length})
                        </Typography>
                        {templateServices.map((templateService) => {
                            const vendors =
                                templateService.template_service_vendors ?? []
                            return (
                                <Box
                                    key={templateService.id}
                                    className={classes.serviceRow}
                                >
                                    <Typography className={classes.serviceName}>
                                        {templateService.service.name}
                                    </Typography>
                                    {vendors.length > 0 && (
                                        <Typography
                                            className={classes.vendorList}
                                        >
                                            {vendors
                                                .map((v) => v.vendor.name)
                                                .join(', ')}
                                        </Typography>
                                    )}
                                </Box>
                            )
                        })}
                    </Box>
                </>
            )}
        </Box>
    )
}
